import { getPolicyDisplayRowsForUser } from '../../lib/pdpa';

function Toggle({ checked, disabled, onChange, label }) {
  return (
    <label className={`pdpa-toggle${disabled ? ' is-disabled' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.checked)}
        aria-label={label}
      />
      <span className="pdpa-toggle-track" aria-hidden="true">
        <span className="pdpa-toggle-thumb" />
      </span>
    </label>
  );
}

function SettingRow({
  icon,
  title,
  description,
  required,
  checked,
  disabled,
  onToggle,
  onReadPolicy,
}) {
  return (
    <div className="pdpa-setting-row">
      <div className="pdpa-setting-icon">
        <i className={`fa-solid ${icon}`}></i>
      </div>
      <div className="pdpa-setting-body">
        <div className="pdpa-setting-title">{title}</div>
        <div className="pdpa-setting-desc">{description}</div>
        {onReadPolicy && (
          <button type="button" className="pdpa-read-link" onClick={onReadPolicy}>
            อ่านนโยบายฉบับเต็ม <i className="fa-solid fa-chevron-right"></i>
          </button>
        )}
      </div>
      <div className="pdpa-setting-control">
        {required ? (
          <span className="pdpa-always-on">เปิดตลอดเวลา</span>
        ) : (
          <Toggle checked={checked} disabled={disabled} onChange={onToggle} label={title} />
        )}
      </div>
    </div>
  );
}

export default function CookieSettingsModal({
  mode,
  userType,
  policyList,
  preferences,
  isSaving,
  canSubmit,
  onClose,
  onChangePreferences,
  onOpenPolicy,
  onAllowAll,
  onRejectOptional,
  onSave,
  onRevoke,
}) {
  const policyRows = getPolicyDisplayRowsForUser(userType, policyList);

  const cookieRows = [
    {
      key: 'necessary',
      icon: 'fa-shield-halved',
      title: 'คุกกี้ที่จำเป็น (Strictly Necessary)',
      description: 'จำเป็นอย่างยิ่งสำหรับการทำงานพื้นฐานของเว็บไซต์',
      required: true,
    },
    {
      key: 'functional',
      icon: 'fa-language',
      title: 'คุกกี้เพื่อการทำงาน (Functional)',
      description: 'จดจำการตั้งค่า เช่น ภาษาและภูมิภาค',
      prefKey: 'functional',
    },
    {
      key: 'performance',
      icon: 'fa-chart-line',
      title: 'คุกกี้ประสิทธิภาพ (Performance)',
      description: 'วิเคราะห์การใช้งานเพื่อปรับปรุงประสบการณ์',
      prefKey: 'performance',
    },
    {
      key: 'marketing',
      icon: 'fa-bullhorn',
      title: 'คุกกี้การตลาด (Marketing)',
      description: 'แสดงเนื้อหาและโฆษณาที่เกี่ยวข้อง',
      prefKey: 'marketing',
    },
  ];

  if (userType === 'employee') {
    cookieRows.push({
      key: 'hr',
      icon: 'fa-users',
      title: 'ระบบงานบุคคล (HR)',
      description: 'รองรับฟีเจอร์ที่เกี่ยวข้องกับข้อมูลพนักงาน',
      prefKey: 'hr',
    });
  }

  const updatePref = (prefKey, value) => {
    onChangePreferences({ ...preferences, [prefKey]: value });
  };

  const title = mode === 'edit' ? 'แก้ไขการตั้งค่าคุกกี้และ PDPA' : 'การตั้งค่าคุกกี้และ PDPA';

  return (
    <div
      className="modal-overlay open"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="modal pdpa-settings-modal" style={{ width: '640px' }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{title}</div>
            <div className="pdpa-settings-subtitle">
              คุณสามารถปรับแต่งคุกกี้เสริมได้ตามต้องการ นโยบายพื้นฐานยังคงต้องเปิดไว้เพื่อการปฏิบัติงาน
            </div>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="ปิด">
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div className="modal-body">
          {policyRows.length > 0 && (
            <div className="pdpa-settings-section">
              {policyRows.map((row) => (
                <SettingRow
                  key={row.versionId}
                  icon={row.icon}
                  title={row.title}
                  description={row.description}
                  required
                  checked
                  onReadPolicy={() => onOpenPolicy(row.versionId)}
                />
              ))}
            </div>
          )}

          <div className="pdpa-settings-heading">การตั้งค่าคุกกี้</div>
          <div className="pdpa-settings-section">
            {cookieRows.map((row) => (
              <SettingRow
                key={row.key}
                icon={row.icon}
                title={row.title}
                description={row.description}
                required={row.required}
                checked={row.required ? true : Boolean(preferences[row.prefKey])}
                disabled={row.required || isSaving}
                onToggle={(value) => updatePref(row.prefKey, value)}
              />
            ))}
          </div>
        </div>

        <div className="modal-footer pdpa-settings-footer">
          <div className="pdpa-settings-footer-row">
            <button
              type="button"
              className="btn btn-ghost pdpa-btn-danger"
              onClick={onRevoke}
              disabled={!canSubmit || isSaving}
            >
              ยกเลิกความยินยอม
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onRejectOptional}
              disabled={!canSubmit || isSaving}
            >
              ปฏิเสธ (คุกกี้เสริม)
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onAllowAll}
              disabled={!canSubmit || isSaving}
            >
              อนุญาตทั้งหมด
            </button>
          </div>
          <button
            type="button"
            className="btn btn-primary pdpa-save-btn"
            onClick={() => onSave(preferences)}
            disabled={!canSubmit || isSaving}
          >
            {isSaving ? <><i className="fa-solid fa-spinner fa-spin"></i> กำลังบันทึก...</> : 'บันทึกการตั้งค่า'}
          </button>
        </div>
      </div>
    </div>
  );
}
